import Button from "../core/Button";
import ResizableScene from "../core/ResizableScene";
import AsyncDraggableButton from "./AsyncDraggableButton";
import DraggableButton from "./DraggableButton";

export type TransformControllerConfig = {
	minScale: number;
	maxScale: number;
};

export default class TransformController extends Phaser.GameObjects.Container {
	static readonly ON_DELETE = "onDelete";
	static readonly ON_CONFIRM = "onConfirm";

	scene: ResizableScene;

	protected transformObj: AsyncDraggableButton = null;
	protected moveOffset: Phaser.Math.Vector2 = new Phaser.Math.Vector2(0, 0);

	protected frame: Phaser.GameObjects.Image;

	// Rotation
	protected rotateConatiner: Phaser.GameObjects.Container;
	protected rotateButton: DraggableButton;

	protected rotationPoint: Phaser.Math.Vector2;
	protected initalRotation: number;

	// Scale
	protected scaleContainer: Phaser.GameObjects.Container;
	protected scaleButton: DraggableButton;
	protected scalePoint: Phaser.Math.Vector2;

	initalObjScale: number;
	protected initalControllerScale: number;
	protected initalDistance: number;

	protected deleteButton: Button;
	protected checkButton: Button;

	protected controlsEnabled = false;

	protected maxObjScale = 10000;
	protected minObjScale = 0;

	protected minControllerScale: number;
	protected maxControllerScale: number;

	constructor(scene: ResizableScene, config: TransformControllerConfig) {
		super(scene);

		this.frame = scene.add.image(0, 0, "transformCircle");
		this.add(this.frame);

		const frameRadius = this.frame.width / 2;

		this.minControllerScale = config.minScale;
		this.maxControllerScale = config.maxScale;

		// this.frame = scene.add.graphics({ lineStyle: { color: 0x231f20, alpha: 1, width: 4 } });
		// this.add(this.frame);
		// this.setFrameSize(this.frameRadius);

		this.setSize(frameRadius, frameRadius);

		this.scaleContainer = scene.add.container(0, 0);
		this.add(this.scaleContainer);

		this.rotateConatiner = scene.add.container(0, 0);
		this.rotateConatiner.setAngle(-45);
		this.scaleContainer.add(this.rotateConatiner);

		this.rotateButton = new DraggableButton(scene, 0, -frameRadius, "rotationArrow");
		this.rotateButton.setOrigin(0.45, 0.381);
		this.rotateButton.setAngle(45);
		// this.rotateButton.setAngle(45);
		this.rotateConatiner.add(this.rotateButton);

		this.rotateButton.on(DraggableButton.ON_DRAG_START, this.startRotate, this);
		this.rotateButton.on(DraggableButton.ON_DRAG, this.onRotate, this);
		this.rotateButton.on(DraggableButton.ON_DRAG_END, this.endRotate, this);

		this.scalePoint = this.findPoint(frameRadius, 45);
		this.scaleButton = new DraggableButton(scene, this.scalePoint.x, this.scalePoint.y, "transformArrow");
		//this.scaleButton.setAngle(90);
		this.scaleContainer.add(this.scaleButton);

		this.scaleButton.on(DraggableButton.ON_DRAG_START, this.startScaling, this);
		this.scaleButton.on(DraggableButton.ON_DRAG, this.onScaling, this);
		this.scaleButton.on(DraggableButton.ON_DRAG_END, this.endScaling, this);

		const point = this.findPoint(frameRadius, -45);
		this.deleteButton = scene.add.button(point.x, point.y, "trashBtn", null, this.trash, this, true);
		this.deleteButton.clickSoundKey = null;
		this.scaleContainer.add(this.deleteButton);

		const checkPoint = this.findPoint(frameRadius, 135);
		const checkBtn = (this.checkButton = scene.add.button(checkPoint.x, checkPoint.y, "confirmBtn", null, this.onConfirm, this, true));
		checkBtn.clickSoundKey = null;
		this.scaleContainer.add(checkBtn);
	}

	protected findPoint(distance: number, angle: number): Phaser.Math.Vector2 {
		const point = new Phaser.Math.Vector2(0, 0);
		point.x = distance * Math.cos(Phaser.Math.DegToRad(angle));
		point.y = distance * Math.sin(Phaser.Math.DegToRad(angle));
		return point;
	}

	setMaxScale(max: number): void {
		this.maxObjScale = max;
	}

	setMinScale(min: number): void {
		this.minObjScale = min;
	}

	resetSettings(): void {
		this.minObjScale = 0.1;
		this.maxObjScale = 5;
	}

	setControllerScale(scale: number): number {
		scale = Phaser.Math.Clamp(scale, this.minControllerScale, this.maxControllerScale);
		this.scaleContainer.setScale(scale);
		this.frame.setScale(scale);
		return scale;
	}

	setTransformObject(transformObj: AsyncDraggableButton): void {
		if (transformObj == null || transformObj == undefined) return;

		if (this.transformObj != null && this.transformObj != undefined) {
			this.removeTransformObject();
		}

		this.transformObj = transformObj;
		this.setPosition(transformObj.x, transformObj.y);
		// transformObj should behind everything
		this.addAt(this.transformObj, 0);
		this.transformObj.setPosition(0, 0);
		if (this.transformObj.texture.key == "whiteSquare") {
			this.setControllerScale(this.transformObj.scale / 10);
		} else {
			this.setControllerScale(this.transformObj.scale);
		}
		this.setActive(true);

		this.transformObj.on(DraggableButton.ON_DRAG_START, this.startDrag, this);
		this.transformObj.on(DraggableButton.ON_DRAG, this.onDrag, this);
		this.transformObj.on(DraggableButton.ON_DRAG_END, this.endDrag, this);
	}

	removeTransformObject(): void {
		if (this.transformObj != null && this.transformObj != undefined) {
			this.transformObj.off(DraggableButton.ON_DRAG_START, this.startDrag, this);
			this.transformObj.off(DraggableButton.ON_DRAG, this.onDrag, this);
			this.transformObj.off(DraggableButton.ON_DRAG_END, this.endDrag, this);
			this.remove(this.transformObj);
			this.transformObj.setPosition(this.x, this.y);
		}
		this.transformObj = null;
	}

	isSelectedObj(obj: AsyncDraggableButton): boolean {
		return this.transformObj == obj;
	}

	// Drag Functions
	startDrag(ptr: Phaser.Input.Pointer): void {
		if (this.transformObj == null || this.transformObj == undefined) return;
		this.scene.soundManager.play("itemDrag");
		this.moveOffset.set(ptr.x - this.x, ptr.y - this.y);
	}

	onDrag(ptr: Phaser.Input.Pointer): void {
		if (this.transformObj == null || this.transformObj == undefined) return;
		this.setPosition(ptr.x - this.moveOffset.x, ptr.y - this.moveOffset.y);
	}

	endDrag(): void {
		if (this.transformObj == null || this.transformObj == undefined) return;
		this.scene.soundManager.play("itemDrag", false, { volume: 0.5 });
		this.moveOffset.set(0, 0);
	}

	// Rotation Functions
	startRotate(ptr: Phaser.Input.Pointer): void {
		if (this.transformObj == null) this.rotateButton.endDrag(null);
		this.deleteButton.setVisible(false);
		this.scaleButton.setVisible(false);
		this.checkButton.setVisible(false);
		this.rotationPoint = new Phaser.Math.Vector2(ptr.x - this.x, ptr.y - this.y);
		this.initalRotation = this.transformObj.rotation;
	}

	onRotate(ptr: Phaser.Input.Pointer): void {
		if (this.transformObj == null) return;

		const initalAngle = Phaser.Math.Angle.Between(0, 0, this.rotationPoint.x, this.rotationPoint.y);

		const newPoint = new Phaser.Math.Vector2(ptr.x - this.x, ptr.y - this.y);
		const newAngle = Phaser.Math.Angle.Between(0, 0, newPoint.x, newPoint.y);
		const angle = newAngle - initalAngle;

		this.rotateConatiner.setRotation(angle + Phaser.Math.DegToRad(-45));
		this.transformObj.setRotation(this.initalRotation + angle);
		// this.rotateButton.y = -Phaser.Math.Distance.Between(ptr.x, ptr.y, this.x, this.y);
	}

	endRotate(): void {
		if (this.transformObj == null) return;
		this.deleteButton.setVisible(true);
		this.scaleButton.setVisible(true);
		this.checkButton.setVisible(true);
		this.rotateConatiner.setAngle(-45);
	}

	// Scaling Functions
	startScaling(ptr: Phaser.Input.Pointer): void {
		if (this.transformObj == null) return;
		this.deleteButton.setVisible(false);
		this.rotateButton.setVisible(false);
		this.checkButton.setVisible(false);
		this.initalDistance = Phaser.Math.Distance.Between(0, 0, ptr.x - this.x, ptr.y - this.y);
		this.initalControllerScale = this.scaleContainer.scale;
		this.initalObjScale = this.transformObj.scale;

		this.scene.soundManager.play("itemScale");
	}

	onScaling(ptr: Phaser.Input.Pointer): void {
		if (this.transformObj == null) return;

		const distance = Phaser.Math.Distance.Between(ptr.x, ptr.y, this.x, this.y);

		const actualScale = Math.abs(distance / this.initalDistance) * this.initalObjScale;
		const objScale = Phaser.Math.Clamp(actualScale, this.minObjScale, this.maxObjScale);
		this.transformObj.setScale(objScale);

		const point = new Phaser.Math.Vector3(ptr.x - this.x, ptr.y - this.y);
		const rotation = Phaser.Math.Angle.Between(0, 0, point.x, point.y);

		this.setControllerScale(Math.abs(distance / this.initalDistance) * this.initalControllerScale);
		const pos = new Phaser.Math.Vector2(ptr.x - this.x, ptr.y - this.y);
		pos.normalize();
		pos.x *= this.width;
		pos.y *= this.height;

		this.scaleButton.setPosition(pos.x, pos.y);
		//this.scaleButton.setRotation(rotation + Phaser.Math.DegToRad(45));
	}

	endScaling(): void {
		if (this.transformObj == null) return;
		this.deleteButton.setVisible(true);
		this.rotateButton.setVisible(true);
		this.checkButton.setVisible(true);
		this.scaleButton.setPosition(this.scalePoint.x, this.scalePoint.y);
		//this.scaleButton.setAngle(90);
		// this.setFrameSize(this.frameRadius);

		this.scene.soundManager.play("itemScale", false, { volume: 0.5 });
	}

	onConfirm(): void {
		if (this.transformObj == null) return;
		this.scene.soundManager.play("click");
		this.emit(TransformController.ON_CONFIRM, this);
	}

	trash(): void {
		//if (this.transformObj == null) return;
		this.scene.soundManager.play("click");
		if(this.transformObj) this.transformObj.destroy();
		this.setActive(false);
		this.emit(TransformController.ON_DELETE, this);
	}

	setActive(active: boolean): this {
		if (!active) {
			this.removeTransformObject();
			this.resetSettings();
		}

		super.setActive(active);
		this.deleteButton.setVisible(true);
		this.rotateButton.setVisible(true);
		this.scaleButton.setVisible(true);
		this.checkButton.setVisible(true);
		this.setVisible(active);
		return this;
	}
}
